import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaSync, FaRubleSign, FaShoppingBag, FaClock, FaCreditCard, FaMoneyBillWave, FaChevronDown, FaChevronRight, FaBoxOpen } from 'react-icons/fa';
import analyticsApiService from '../services/analyticsApi';

const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return new Intl.NumberFormat('ru-RU').format(Math.round(num));
};

const formatCurrency = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0 ₽';
  return `${formatNumber(num)} ₽`;
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch (e) {
    return iso;
  }
};

const formatTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return iso;
  }
};

const formatShiftDate = (iso) => {
  if (!iso) return '—';
  try {
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
  } catch (e) {
    return iso;
  }
};

const toISODate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const todayISO = () => toISODate(new Date());

const daysAgoISO = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return toISODate(d);
};

const MODES = {
  ALL: 'all',
  SINGLE: 'single',
  RANGE: 'range',
};

// KPI карточка (адаптивна)
const KpiCard = ({ label, value, sub, icon: Icon, color = 'text-gray-600' }) => (
  <div className="bg-white p-3 sm:p-4 md:p-5 rounded-xl shadow-sm border">
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{label}</p>
        <p className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 break-words leading-tight">{value}</p>
        {sub && <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">{sub}</p>}
      </div>
      {Icon && <Icon className={`w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8 shrink-0 ${color}`} />}
    </div>
  </div>
);

const ShiftReport = ({ shift }) => {
  const dishes = shift.dishes || [];
  const cats = shift.categories || [];
  const types = shift.order_types || [];
  const isEmpty = (shift.orders_count || 0) === 0;

  if (isEmpty) {
    return (
      <div className="bg-white rounded-lg border p-6 text-center text-gray-500 text-sm">
        За эту смену заказов нет
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Финансовые карточки смены */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 sm:p-3">
          <p className="text-[11px] sm:text-xs text-emerald-700">Выручка</p>
          <p className="text-sm sm:text-lg font-bold text-emerald-900 break-words leading-tight">{formatCurrency(shift.total_revenue)}</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 sm:p-3">
          <p className="text-[11px] sm:text-xs text-blue-700">Заказов</p>
          <p className="text-sm sm:text-lg font-bold text-blue-900">{formatNumber(shift.orders_count)}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 sm:p-3">
          <p className="text-[11px] sm:text-xs text-amber-700">Продано шт.</p>
          <p className="text-sm sm:text-lg font-bold text-amber-900">{formatNumber(shift.items_sold)}</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5 sm:p-3">
          <p className="text-[11px] sm:text-xs text-purple-700">Средний чек</p>
          <p className="text-sm sm:text-lg font-bold text-purple-900 break-words leading-tight">{formatCurrency(shift.avg_check)}</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 sm:p-3">
          <p className="text-[11px] sm:text-xs text-green-700">Наличные</p>
          <p className="text-sm sm:text-lg font-bold text-green-900 break-words leading-tight">{formatCurrency(shift.cash_revenue)}</p>
          <p className="text-[10px] text-green-700">{formatNumber(shift.cash_orders)} зак.</p>
        </div>
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-2.5 sm:p-3">
          <p className="text-[11px] sm:text-xs text-indigo-700">Карта</p>
          <p className="text-sm sm:text-lg font-bold text-indigo-900 break-words leading-tight">{formatCurrency(shift.card_revenue)}</p>
          <p className="text-[10px] text-indigo-700">{formatNumber(shift.card_orders)} зак.</p>
        </div>
      </div>

      {/* Время первого/последнего заказа */}
      {(shift.first_order_at || shift.last_order_at) && (
        <div className="bg-white rounded-lg border p-3 text-xs sm:text-sm text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
          {shift.first_order_at && (
            <div>Первый: <span className="font-medium text-gray-900">{formatDateTime(shift.first_order_at)}</span></div>
          )}
          {shift.last_order_at && (
            <div>Последний: <span className="font-medium text-gray-900">{formatDateTime(shift.last_order_at)}</span></div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        {/* Типы заказов */}
        {types.length > 0 && (
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="px-3 py-2 bg-gray-100 text-sm font-semibold text-gray-700">Типы заказов</div>
            <div className="divide-y">
              {types.map((t) => (
                <div key={t.order_type} className="px-3 py-2 flex items-center justify-between gap-2 text-sm">
                  <div className="text-gray-900 truncate">{t.order_type_display}</div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-gray-500">{formatNumber(t.count)} зак.</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(t.revenue)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Категории */}
        {cats.length > 0 && (
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="px-3 py-2 bg-gray-100 text-sm font-semibold text-gray-700">По категориям</div>
            <div className="divide-y">
              {cats.map((c) => (
                <div key={c.category_name} className="px-3 py-2 flex items-center justify-between gap-2 text-sm">
                  <div className="text-gray-900 truncate">{c.category_name}</div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-gray-500">{formatNumber(c.quantity)} шт</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(c.revenue)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Проданные блюда */}
      {dishes.length > 0 && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="px-3 py-2 bg-gray-100 text-sm font-semibold text-gray-700">
            Проданные блюда ({dishes.length})
          </div>

          {/* Mobile: карточный список */}
          <div className="divide-y md:hidden">
            {dishes.map((d, idx) => {
              const share = shift.total_revenue > 0 ? (d.revenue / shift.total_revenue) * 100 : 0;
              return (
                <div key={d.dish_id ?? `${d.dish_name}-${idx}`} className="px-3 py-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 text-sm truncate">{idx + 1}. {d.dish_name}</div>
                      <div className="text-xs text-gray-500 truncate">{d.category_name}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold text-gray-900">{formatCurrency(d.revenue)}</div>
                      <div className="text-xs text-gray-500">{formatNumber(d.quantity)} шт • {share.toFixed(1)}%</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: таблица */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Блюдо</th>
                  <th className="px-3 py-2 text-left">Категория</th>
                  <th className="px-3 py-2 text-right">Кол-во</th>
                  <th className="px-3 py-2 text-right">Сумма</th>
                  <th className="px-3 py-2 text-right">Доля</th>
                </tr>
              </thead>
              <tbody>
                {dishes.map((d, idx) => {
                  const share = shift.total_revenue > 0 ? (d.revenue / shift.total_revenue) * 100 : 0;
                  return (
                    <tr key={d.dish_id ?? `${d.dish_name}-${idx}`} className="border-t">
                      <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                      <td className="px-3 py-2 font-medium text-gray-900">{d.dish_name}</td>
                      <td className="px-3 py-2 text-gray-700">{d.category_name}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{formatNumber(d.quantity)} шт</td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900">{formatCurrency(d.revenue)}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{share.toFixed(1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Карточка смены (mobile)
const ShiftCard = ({ shift, isOpen, onToggle }) => {
  return (
    <div className="bg-white border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-3 py-3 active:bg-gray-50"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-gray-400 text-sm">
                {isOpen ? <FaChevronDown /> : <FaChevronRight />}
              </span>
              <span className="font-semibold text-gray-900">{formatShiftDate(shift.shift_date)}</span>
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5 ml-5">
              {formatTime(shift.shift_start)} → {formatTime(shift.shift_end)}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-bold text-gray-900 text-base">{formatCurrency(shift.total_revenue)}</div>
            <div className="text-[11px] text-gray-500">{formatNumber(shift.orders_count)} зак. • {formatNumber(shift.items_sold)} шт</div>
          </div>
        </div>

        <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
          <div className="bg-green-50 rounded p-1.5">
            <div className="text-green-700">Наличные</div>
            <div className="font-semibold text-green-900 text-sm">{formatCurrency(shift.cash_revenue)}</div>
          </div>
          <div className="bg-indigo-50 rounded p-1.5">
            <div className="text-indigo-700">Карта</div>
            <div className="font-semibold text-indigo-900 text-sm">{formatCurrency(shift.card_revenue)}</div>
          </div>
          <div className="bg-purple-50 rounded p-1.5">
            <div className="text-purple-700">Ср. чек</div>
            <div className="font-semibold text-purple-900 text-sm">{formatCurrency(shift.avg_check)}</div>
          </div>
        </div>
      </button>

      {isOpen && (
        <div className="border-t bg-gray-50 p-3">
          <ShiftReport shift={shift} />
        </div>
      )}
    </div>
  );
};

const ShiftStats = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [mode, setMode] = useState(MODES.ALL);
  const [date, setDate] = useState(todayISO());
  const [dateFrom, setDateFrom] = useState(daysAgoISO(30));
  const [dateTo, setDateTo] = useState(todayISO());

  const [selectedEstablishment, setSelectedEstablishment] = useState('all');
  const [establishments, setEstablishments] = useState([]);

  const [data, setData] = useState(null);
  const [expandedShifts, setExpandedShifts] = useState({});

  const loadEstablishments = useCallback(async () => {
    try {
      const list = await analyticsApiService.getEstablishments();
      setEstablishments(Array.isArray(list) ? list : []);
    } catch (e) {
      // не критично
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        establishment: selectedEstablishment !== 'all' ? selectedEstablishment : undefined,
      };
      if (mode === MODES.SINGLE) {
        params.date = date;
      } else if (mode === MODES.RANGE) {
        params.date_from = dateFrom;
        params.date_to = dateTo;
      } else {
        params.date_from = daysAgoISO(30);
        params.date_to = todayISO();
      }
      const resp = await analyticsApiService.getShiftStats(params);
      setData(resp);
    } catch (e) {
      setError('Не удалось загрузить статистику по сменам');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [mode, date, dateFrom, dateTo, selectedEstablishment]);

  useEffect(() => {
    loadEstablishments();
  }, [loadEstablishments]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const summary = data?.summary || {};
  const shifts = data?.shifts || [];

  const shiftsSorted = useMemo(() => {
    return [...shifts].sort((a, b) => (a.shift_date < b.shift_date ? 1 : -1));
  }, [shifts]);

  const headerSubtitle = useMemo(() => {
    const startH = data?.shift_start_hour ?? 13;
    const endH = data?.shift_end_hour ?? 2;
    const sh = String(startH).padStart(2, '0');
    const eh = String(endH).padStart(2, '0');
    return `Смена: ${sh}:00 → ${eh}:00 МСК`;
  }, [data]);

  const toggleShift = (key) => {
    setExpandedShifts(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Заголовок */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Статистика смены</h1>
        <p className="text-xs sm:text-sm text-gray-600">{headerSubtitle}</p>
      </div>

      {/* Фильтры */}
      <div className="bg-white rounded-xl shadow-sm border p-3 sm:p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
          <select
            value={selectedEstablishment}
            onChange={(e) => setSelectedEstablishment(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="all">Все заведения</option>
            {establishments.map((est) => (
              <option key={est.id} value={est.id}>{est.name}</option>
            ))}
          </select>

          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value={MODES.ALL}>За всё (30 дней)</option>
            <option value={MODES.SINGLE}>Одна смена</option>
            <option value={MODES.RANGE}>Диапазон</option>
          </select>

          {mode === MODES.SINGLE && (
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:col-span-2"
              title="Дата открытия смены (13:00)"
            />
          )}

          {mode === MODES.RANGE && (
            <>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                title="С какой даты"
              />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                title="По какую дату"
              />
            </>
          )}

          <button
            onClick={loadData}
            disabled={loading}
            className={`flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm ${
              mode === MODES.ALL ? 'sm:col-span-2 lg:col-span-2' : ''
            }`}
          >
            <FaSync className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Обновить
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Итоговые KPI */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
        <KpiCard label="Выручка" value={formatCurrency(summary.total_revenue)} icon={FaRubleSign} color="text-emerald-600" />
        <KpiCard label="Заказов" value={formatNumber(summary.total_orders)} icon={FaShoppingBag} color="text-blue-600" />
        <KpiCard label="Средний чек" value={formatCurrency(summary.avg_check)} icon={FaRubleSign} color="text-purple-600" />
        <KpiCard label="Продано шт." value={formatNumber(summary.items_sold)} icon={FaBoxOpen} color="text-amber-600" />
        <KpiCard label="Смен" value={formatNumber(summary.shifts_count)} icon={FaClock} color="text-orange-600" />
      </div>

      {/* Способы оплаты */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4">
        <KpiCard
          label="Наличные"
          value={formatCurrency(summary.cash_revenue)}
          sub={`${formatNumber(summary.cash_orders)} заказов`}
          icon={FaMoneyBillWave}
          color="text-emerald-600"
        />
        <KpiCard
          label="Карта"
          value={formatCurrency(summary.card_revenue)}
          sub={`${formatNumber(summary.card_orders)} заказов`}
          icon={FaCreditCard}
          color="text-blue-600"
        />
      </div>

      {/* Смены */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-3 sm:px-4 py-3 border-b border-gray-200">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">Смены</h2>
          <p className="text-xs sm:text-sm text-gray-500">Нажмите на смену, чтобы открыть отчёт</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48 sm:h-64">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : shiftsSorted.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-gray-500">
            Нет данных за выбранный период
          </div>
        ) : (
          <>
            {/* Mobile: карточный список */}
            <div className="md:hidden p-3 space-y-2">
              {shiftsSorted.map((s) => (
                <ShiftCard
                  key={s.shift_date}
                  shift={s}
                  isOpen={!!expandedShifts[s.shift_date]}
                  onToggle={() => toggleShift(s.shift_date)}
                />
              ))}
            </div>

            {/* Desktop: таблица */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 w-8"></th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Дата смены</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Период</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Заказов</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Продано, шт</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Выручка</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Средний чек</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Наличные</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Карта</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {shiftsSorted.map((s) => {
                    const isOpen = !!expandedShifts[s.shift_date];
                    return (
                      <React.Fragment key={s.shift_date}>
                        <tr
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => toggleShift(s.shift_date)}
                        >
                          <td className="px-4 py-3 text-gray-400">
                            {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{formatShiftDate(s.shift_date)}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            <div>{formatDateTime(s.shift_start)}</div>
                            <div className="text-gray-500">— {formatDateTime(s.shift_end)}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatNumber(s.orders_count)}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatNumber(s.items_sold)}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">{formatCurrency(s.total_revenue)}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatCurrency(s.avg_check)}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 text-right">
                            <div>{formatCurrency(s.cash_revenue)}</div>
                            <div className="text-xs text-gray-500">{formatNumber(s.cash_orders)} зак.</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 text-right">
                            <div>{formatCurrency(s.card_revenue)}</div>
                            <div className="text-xs text-gray-500">{formatNumber(s.card_orders)} зак.</div>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-gray-50">
                            <td></td>
                            <td colSpan={8} className="px-4 py-4">
                              <ShiftReport shift={s} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShiftStats;
